/**
 * Billing page - Usage and subscription management
 * 
 * Per design.md: Use @uprosal/ui components, semantic tokens, lucide icons
 */

import { motion } from 'framer-motion';
import {
    Card, CardContent, CardHeader, CardTitle,
    Button, Badge,
    CreditCard, Zap,
} from '@uprosal/ui';

export function BillingPage() {
    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-foreground">Billing</h1>
                <p className="text-muted-foreground mt-1">
                    Manage your subscription and view usage
                </p>
            </div>

            {/* Current plan */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Zap className="w-5 h-5 text-primary" />
                                    <h2 className="text-lg font-semibold text-foreground">
                                        Pro Plan
                                    </h2>
                                    <Badge variant="success">Active</Badge>
                                </div>
                                <p className="text-muted-foreground mb-4">
                                    $29/month Â· Renews on Feb 13, 2026
                                </p>
                            </div>
                            <Button variant="outline">
                                Manage Plan
                            </Button>
                        </div>

                        {/* Usage bars */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-border">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-foreground">AI Tokens</span>
                                    <span className="text-sm text-muted-foreground">35,000 / 100,000</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2">
                                    <div
                                        className="bg-primary h-2 rounded-full transition-all duration-500"
                                        style={{ width: '35%' }}
                                    />
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-foreground">Proposals</span>
                                    <span className="text-sm text-muted-foreground">47 / 200</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2">
                                    <div
                                        className="bg-chart-1 h-2 rounded-full transition-all duration-500"
                                        style={{ width: '23.5%' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Payment method */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
            >
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Payment Method</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between p-4 rounded-lg bg-muted">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-background">
                                    <CreditCard className="w-5 h-5 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="font-medium text-foreground">â€¢â€¢â€¢â€¢ â€¢â€¢â€¢â€¢ â€¢â€¢â€¢â€¢ 4242</p>
                                    <p className="text-sm text-muted-foreground">Expires 12/28</p>
                                </div>
                            </div>
                            <Button variant="ghost" size="sm">
                                Update
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Enterprise upgrade CTA - Postponed for MVP
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
            >
                <Card className="border-primary/20 bg-primary/5">
                    <CardContent className="pt-6">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-xl bg-primary/20">
                                    <Zap className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-foreground">Need more tokens?</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Upgrade to Enterprise for unlimited AI usage
                                    </p>
                                </div>
                            </div>
                            <Button className="flex items-center gap-2">
                                Upgrade
                                <ArrowRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
            */}
        </div>
    );
}
